import React, { useState, useRef, useCallback } from 'react';
import { formatBytes } from '../../utils/formatBytes';
import { getFileIcon } from '../../utils/fileIcon';

// Default configuration
const DEFAULT_MAX_BYTES = 50 * 1024 * 1024; // 50MB
const DEFAULT_ALLOW_MIMES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/zip',
  'application/x-zip-compressed',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'text/plain',
  'text/csv',
];

/**
 * File upload queue item states
 */
const UPLOAD_STATE = {
  QUEUED: 'queued',
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  AVAILABLE: 'available',
  ERROR: 'error',
};

/**
 * AttachmentsUploader Component
 * Drag & drop multi-file uploader with validation and progress
 */
export function AttachmentsUploader({
  orderId,
  kind = 'deliverable',
  messageId = null,
  onUploaded = null,
  maxBytes = DEFAULT_MAX_BYTES,
  allowMimes = DEFAULT_ALLOW_MIMES,
  uploadFile, // From useAttachments hook
  disabled = false,
}) {
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Validate file
  const validateFile = useCallback((file) => {
    const errors = [];

    // Check size
    if (file.size === 0) {
      errors.push('File is empty');
    } else if (file.size > maxBytes) {
      errors.push(`File size (${formatBytes(file.size)}) exceeds maximum (${formatBytes(maxBytes)})`);
    }

    // Check mime type
    if (!allowMimes.includes(file.type)) {
      errors.push(`File type "${file.type}" is not allowed`);
    }

    return errors;
  }, [maxBytes, allowMimes]);

  // Handle file selection
  const handleFiles = useCallback(async (files) => {
    const fileArray = Array.from(files);
    
    // Create queue items
    const queueItems = fileArray.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      state: UPLOAD_STATE.QUEUED,
      progress: 0,
      errors: validateFile(file),
    }));

    setUploadQueue(prev => [...prev, ...queueItems]);

    // Upload valid files
    for (const item of queueItems) {
      if (item.errors.length === 0) {
        await uploadSingleFile(item);
      }
    }
  }, [validateFile]);

  // Upload single file
  const uploadSingleFile = async (queueItem) => {
    try {
      // Update state to uploading
      setUploadQueue(prev => prev.map(item =>
        item.id === queueItem.id
          ? { ...item, state: UPLOAD_STATE.UPLOADING, progress: 50 }
          : item
      ));

      // Upload file
      const uploadedFile = await uploadFile(queueItem.file, {
        kind,
        messageId,
      });

      // Update state to processing
      setUploadQueue(prev => prev.map(item =>
        item.id === queueItem.id
          ? { ...item, state: UPLOAD_STATE.PROCESSING, progress: 75, uploadedFile }
          : item
      ));

      // Call callback
      if (onUploaded) {
        onUploaded(uploadedFile);
      }

      // Wait a bit then mark as available
      setTimeout(() => {
        setUploadQueue(prev => prev.map(item =>
          item.id === queueItem.id
            ? { ...item, state: UPLOAD_STATE.AVAILABLE, progress: 100 }
            : item
        ));

        // Remove from queue after 3 seconds
        setTimeout(() => {
          setUploadQueue(prev => prev.filter(item => item.id !== queueItem.id));
        }, 3000);
      }, 1000);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadQueue(prev => prev.map(item =>
        item.id === queueItem.id
          ? { ...item, state: UPLOAD_STATE.ERROR, errors: [error.message], progress: 0 }
          : item
      ));
    }
  };

  // Retry failed upload
  const retryUpload = (queueItem) => {
    setUploadQueue(prev => prev.map(item =>
      item.id === queueItem.id
        ? { ...item, state: UPLOAD_STATE.QUEUED, errors: [], progress: 0 }
        : item
    ));
    uploadSingleFile(queueItem);
  };

  // Remove from queue
  const removeFromQueue = (itemId) => {
    setUploadQueue(prev => prev.filter(item => item.id !== itemId));
  };

  // File input change
  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = ''; // Reset input
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // Keyboard handler for drag zone
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const isUploading = uploadQueue.some(item => item.state === UPLOAD_STATE.UPLOADING);

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${isDragActive ? 'border-amber-500 bg-amber-50' : 'border-gray-300 bg-gray-50'}
          ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-amber-400'}
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={disabled || isUploading ? -1 : 0}
        aria-label="Upload files"
        aria-disabled={disabled || isUploading}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={allowMimes.join(',')}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled || isUploading}
          aria-label="Select files to upload"
        />
        
        <div className="space-y-2">
          <div className="text-4xl">📎</div>
          <p className="text-lg font-medium text-gray-700">
            {isDragActive ? 'Drop files here' : 'Drag & drop files or click to browse'}
          </p>
          <p className="text-sm text-gray-500">
            Maximum file size: {formatBytes(maxBytes)}
          </p>
          <p className="text-xs text-gray-400">
            Supported: Images, PDFs, Documents
          </p>
        </div>
      </div>

      {/* Upload queue */}
      {uploadQueue.length > 0 && (
        <div 
          className="space-y-2"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <h4 className="text-sm font-medium text-gray-700">Upload Queue</h4>
          {uploadQueue.map(item => (
            <div
              key={item.id}
              className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg"
            >
              {/* File icon */}
              <div className="text-2xl flex-shrink-0">
                {getFileIcon(item.file.type)}
              </div>

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {item.file.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatBytes(item.file.size)}
                </p>

                {/* Progress bar */}
                {item.state === UPLOAD_STATE.UPLOADING || item.state === UPLOAD_STATE.PROCESSING ? (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${item.progress}%` }}
                        role="progressbar"
                        aria-valuenow={item.progress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {item.state === UPLOAD_STATE.UPLOADING ? 'Uploading...' : 'Processing...'}
                    </p>
                  </div>
                ) : null}

                {/* Errors */}
                {item.errors.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {item.errors.map((error, idx) => (
                      <p key={idx} className="text-xs text-red-600">
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Status badge */}
              <div className="flex-shrink-0">
                {item.state === UPLOAD_STATE.QUEUED && (
                  <span className="text-xs text-gray-500">Queued</span>
                )}
                {item.state === UPLOAD_STATE.UPLOADING && (
                  <span className="text-xs text-blue-600">Uploading</span>
                )}
                {item.state === UPLOAD_STATE.PROCESSING && (
                  <span className="text-xs text-amber-600">Processing</span>
                )}
                {item.state === UPLOAD_STATE.AVAILABLE && (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <span>✓</span> Done
                  </span>
                )}
                {item.state === UPLOAD_STATE.ERROR && (
                  <button
                    onClick={() => retryUpload(item)}
                    className="text-xs text-red-600 hover:text-red-700 underline"
                    aria-label={`Retry upload ${item.file.name}`}
                  >
                    Retry
                  </button>
                )}
              </div>

              {/* Remove button */}
              {(item.state === UPLOAD_STATE.ERROR || item.errors.length > 0) && (
                <button
                  onClick={() => removeFromQueue(item.id)}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                  aria-label={`Remove ${item.file.name} from queue`}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
